import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Download, RefreshCw, X, Filter, TrendingUp, History } from 'lucide-react';

const PAGE_SIZE = 25;

/**
 * Payment Configuration Audit — searchable + filterable + exportable view
 * over the `secure_settings_audit` collection.
 *
 * Props:
 *   authedFetch(path, opts?) → JSON  — must exist on parent, includes bearer token
 *   API                            — base URL for the CSV export href
 *   token                          — bearer token so the CSV download is authenticated
 *
 * All expanded rows are memoised. All queries are debounced 250 ms.
 */
export default function AuditLogPanel({ authedFetch, API, token }) {
  const [items, setItems]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [actions, setActions]       = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  // Filters
  const [action, setAction]         = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [q, setQ]                   = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [skip, setSkip]             = useState(0);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (action)     params.set('action', action);
    if (adminEmail) params.set('admin_email', adminEmail.trim());
    if (q)          params.set('q', q.trim());
    if (dateFrom)   params.set('date_from', new Date(dateFrom).toISOString());
    if (dateTo)     params.set('date_to', new Date(dateTo).toISOString());
    params.set('limit', String(PAGE_SIZE));
    params.set('skip', String(skip));
    return params.toString();
  }, [action, adminEmail, q, dateFrom, dateTo, skip]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await authedFetch(`/api/admin/audit-log?${buildQuery()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authedFetch, buildQuery]);

  const loadMetadata = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([
        authedFetch('/api/admin/audit-log/actions'),
        authedFetch('/api/admin/audit-log/summary'),
      ]);
      setActions(a.actions || []);
      setSummary(s);
    } catch { /* non-fatal */ }
  }, [authedFetch]);

  // Debounced load on filter change
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { loadMetadata(); }, [loadMetadata]);

  const resetFilters = () => {
    setAction(''); setAdminEmail(''); setQ(''); setDateFrom(''); setDateTo(''); setSkip(0);
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const hasFilters = action || adminEmail || q || dateFrom || dateTo;

  // CSV export — must send bearer token; use fetch + blob so browser saves the file
  const exportCsv = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (action)     params.set('action', action);
      if (adminEmail) params.set('admin_email', adminEmail.trim());
      if (q)          params.set('q', q.trim());
      if (dateFrom)   params.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo)     params.set('date_to', new Date(dateTo).toISOString());
      const res = await fetch(`${API}/api/admin/audit-log/export.csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19) + 'Z';
      a.download = `sag-audit-${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const pageEnd = Math.min(skip + items.length, total);
  const canPrev = skip > 0;
  const canNext = skip + items.length < total;

  return (
    <section className="bg-white border border-gray-200 p-6" data-testid="audit-panel">
      {/* Header + stats */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h3 className="text-sm font-medium text-black flex items-center gap-2">
            <History className="w-4 h-4 text-[#1e3a5f]" />
            Payment Configuration Audit
          </h3>
          <p className="text-[11px] text-gray-500 mt-1 max-w-md">
            Every key rotation, checkout failure, admin login, and password change is written here.
            Filter, search, or export to CSV — no plaintext credentials are ever stored.
          </p>
        </div>
        {summary && (
          <div className="flex items-center gap-3 text-[11px]">
            <StatChip label="Total" value={summary.total} data-testid="audit-stat-total" />
            <StatChip label="Last 24h" value={sumWindow(summary.last_24h)} data-testid="audit-stat-24h" />
            <StatChip label="Last 7d" value={sumWindow(summary.last_7d)} data-testid="audit-stat-7d" />
          </div>
        )}
      </div>

      {/* Filters row */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4" data-testid="audit-filters">
        <div className="relative md:col-span-2">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setSkip(0); }}
            placeholder="Search action / admin / IP…"
            className="w-full pl-8 pr-2 py-1.5 border border-gray-300 rounded text-xs focus:border-[#1e3a5f] focus:outline-none"
            data-testid="audit-filter-search"
          />
        </div>
        <select
          value={action}
          onChange={(e) => { setAction(e.target.value); setSkip(0); }}
          className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:border-[#1e3a5f] focus:outline-none"
          data-testid="audit-filter-action"
        >
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input
          type="email"
          value={adminEmail}
          onChange={(e) => { setAdminEmail(e.target.value); setSkip(0); }}
          placeholder="admin email"
          className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-[#1e3a5f] focus:outline-none"
          data-testid="audit-filter-email"
        />
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setSkip(0); }}
          className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-[#1e3a5f] focus:outline-none"
          data-testid="audit-filter-from"
          title="From date"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setSkip(0); }}
          className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-[#1e3a5f] focus:outline-none"
          data-testid="audit-filter-to"
          title="To date"
        />
      </div>

      {/* Actions row */}
      <div className="flex items-center gap-2 mb-4 text-xs">
        {hasFilters && (
          <button
            onClick={resetFilters}
            className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 text-gray-600 hover:bg-gray-50"
            data-testid="audit-reset"
          >
            <X className="w-3 h-3" /> Clear filters
          </button>
        )}
        <button
          onClick={() => { loadMetadata(); load(); }}
          className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 text-gray-600 hover:bg-gray-50"
          data-testid="audit-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <button
          onClick={exportCsv}
          disabled={loading || total === 0}
          className="inline-flex items-center gap-1 px-2 py-1 bg-[#1e3a5f] text-white hover:bg-[#152a45] disabled:opacity-40 disabled:cursor-not-allowed ml-auto"
          data-testid="audit-export-csv"
        >
          <Download className="w-3 h-3" /> Export CSV
        </button>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 text-xs text-red-700" data-testid="audit-error">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-gray-100">
        <table className="w-full text-xs" data-testid="audit-log-table">
          <thead className="bg-gray-50">
            <tr className="text-left text-gray-500 uppercase tracking-wider">
              <th className="px-3 py-2 w-6"></th>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Admin</th>
              <th className="px-3 py-2">Field</th>
              <th className="px-3 py-2">Last 4</th>
              <th className="px-3 py-2">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400" data-testid="audit-empty">
                {hasFilters ? 'No matching audit entries.' : 'No audit entries yet.'}
              </td></tr>
            )}
            {items.map((row) => (
              <RowWithDetail
                key={row.id}
                row={row}
                expanded={expandedIds.has(row.id)}
                onToggle={() => toggleExpanded(row.id)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-[11px] text-gray-500" data-testid="audit-pagination">
        <span>
          {total === 0 ? '0 events' : `${skip + 1}–${pageEnd} of ${total} events`}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            disabled={!canPrev || loading}
            className="px-2 py-1 border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="audit-prev"
          >Prev</button>
          <button
            onClick={() => setSkip(skip + PAGE_SIZE)}
            disabled={!canNext || loading}
            className="px-2 py-1 border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="audit-next"
          >Next</button>
        </div>
      </div>
    </section>
  );
}

const RowWithDetail = ({ row, expanded, onToggle }) => {
  const hasExtra = row.extra && Object.keys(row.extra).length > 0;
  const when = row.timestamp ? new Date(row.timestamp).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }) : '—';
  return (
    <>
      <tr className={`${hasExtra ? 'cursor-pointer hover:bg-gray-50' : ''}`} onClick={hasExtra ? onToggle : undefined}>
        <td className="px-3 py-2 text-gray-400 select-none">{hasExtra ? (expanded ? '▾' : '▸') : ''}</td>
        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{when}</td>
        <td className="px-3 py-2 font-mono text-[11px] text-gray-700">{row.action || '—'}</td>
        <td className="px-3 py-2 text-gray-700">{row.admin_email || '—'}</td>
        <td className="px-3 py-2 text-gray-500">{row.field || '—'}</td>
        <td className="px-3 py-2 font-mono text-gray-500">{row.last4 ? `••••${row.last4}` : '—'}</td>
        <td className="px-3 py-2 text-gray-400">{row.ip || '—'}</td>
      </tr>
      {expanded && hasExtra && (
        <tr className="bg-gray-50/60">
          <td></td>
          <td colSpan={6} className="px-3 py-2">
            <pre className="text-[10px] font-mono text-gray-700 whitespace-pre-wrap break-all" data-testid={`audit-extra-${row.id}`}>
{JSON.stringify(row.extra, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
};

const StatChip = ({ label, value, ...rest }) => (
  <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-50 border border-gray-200 rounded" {...rest}>
    <TrendingUp className="w-3 h-3 text-[#1e3a5f]" />
    <span className="text-gray-500">{label}:</span>
    <span className="font-semibold text-black">{value ?? 0}</span>
  </div>
);

// summary.last_24h and .last_7d are arrays of {action, count} — reduce to a total
const sumWindow = (arr) => Array.isArray(arr) ? arr.reduce((n, r) => n + (r.count || 0), 0) : 0;
